import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Bed, Bath, Square, MapPin, Calendar, Home, Share2, Play, ChevronLeft, ChevronRight, X, Phone, Mail, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { FEATURED_LISTINGS, COMPANY_INFO } from '../data/mockData';
import { ddfAPI } from '../services/api';
import LeadCaptureForm from '../components/forms/LeadCaptureForm';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isFavorite, addFavorite, removeFavorite } = useAuth();

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      try {
        // Try fetching from DDF API first
        const ddfProperty = await ddfAPI.getById(id);
        if (ddfProperty) {
          setProperty(ddfProperty);
        } else {
          // Fallback to mock data
          const mockProperty = FEATURED_LISTINGS.find(p => p.id === id);
          setProperty(mockProperty);
        }
      } catch (err) {
        console.log('Using mock data:', err);
        const mockProperty = FEATURED_LISTINGS.find(p => p.id === id);
        setProperty(mockProperty);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h1>
          <Link to="/search">
            <Button className="bg-red-600 hover:bg-red-700">Back to Search</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Format price display
  const formatPrice = () => {
    if (property.priceLabel === 'contact' || property.price === 0) {
      return 'Contact for Price';
    }
    if (property.priceLabel === 'lease') {
      return `$${property.price.toLocaleString()}/mo`;
    }
    return `$${property.price.toLocaleString()}`;
  };

  const handleFavorite = () => {
    if (!user) return;
    if (isFavorite(property.id)) {
      removeFavorite(property.id);
    } else {
      addFavorite(property.id);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  const handleVirtualTour = () => {
    if (property.listingUrl) {
      window.open(property.listingUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Image Gallery */}
      <div className="relative h-[60vh] bg-black">
        <img
          src={property.images[currentImageIndex]}
          alt={property.address}
          className="w-full h-full object-cover"
        />
        
        {/* Navigation Arrows */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
          {currentImageIndex + 1} / {property.images.length}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <Button
            variant="outline"
            className="bg-white/90 hover:bg-white"
            onClick={handleFavorite}
          >
            <Heart 
              size={18} 
              className={isFavorite(property.id) ? 'fill-red-500 text-red-500' : ''}
            />
          </Button>
          <Button variant="outline" className="bg-white/90 hover:bg-white">
            <Share2 size={18} />
          </Button>
        </div>

        {/* Status Badge */}
        <Badge className={`absolute top-4 left-4 text-lg px-4 py-1 ${property.priceLabel === 'lease' ? 'bg-blue-600' : 'bg-green-600'}`}>
          {property.priceLabel === 'lease' ? 'For Lease' : property.status}
        </Badge>
      </div>

      {/* Thumbnail Strip */}
      {property.images.length > 1 && (
        <div className="bg-black py-2 px-4 overflow-x-auto">
          <div className="flex gap-2 max-w-7xl mx-auto">
            {property.images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`w-20 h-14 flex-shrink-0 rounded overflow-hidden border-2 transition-colors ${
                  idx === currentImageIndex ? 'border-red-500' : 'border-transparent'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.address}</h1>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={18} className="text-red-600" />
                    <span>{property.city}, {property.province} {property.postalCode}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-red-600">{formatPrice()}</div>
                  <div className="text-gray-500 text-sm">MLS® {property.mlsNumber}</div>
                </div>
              </div>

              {/* Key Stats */}
              <div className="flex flex-wrap gap-6 py-4 border-y">
                <div className="flex items-center gap-2">
                  <Bed size={20} className="text-gray-400" />
                  <span className="text-lg font-semibold">{property.beds}</span>
                  <span className="text-gray-500">Beds</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath size={20} className="text-gray-400" />
                  <span className="text-lg font-semibold">{property.baths}</span>
                  <span className="text-gray-500">Baths</span>
                </div>
                {property.sqft > 0 && (
                  <div className="flex items-center gap-2">
                    <Square size={20} className="text-gray-400" />
                    <span className="text-lg font-semibold">{property.sqft.toLocaleString()}</span>
                    <span className="text-gray-500">Sq Ft</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Home size={20} className="text-gray-400" />
                  <span className="text-gray-500">{property.propertyType}</span>
                </div>
                {property.yearBuilt && (
                  <div className="flex items-center gap-2">
                    <Calendar size={20} className="text-gray-400" />
                    <span className="text-gray-500">Built {property.yearBuilt}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Property</h2>
              <p className="text-gray-600 leading-relaxed">{property.description}</p>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {property.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <span className="w-2 h-2 bg-red-600 rounded-full" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Details */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Property Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Property Type</span>
                  <span className="font-medium">{property.propertyType}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Year Built</span>
                  <span className="font-medium">{property.yearBuilt}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Lot Size</span>
                  <span className="font-medium">{property.lotSize}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Days on Market</span>
                  <span className="font-medium">{property.daysOnMarket} days</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Interested in this property?</h3>
                <LeadCaptureForm 
                  variant="compact"
                  title="Request a Showing"
                  subtitle=""
                />
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-3">Or contact us directly:</p>
                  <div className="space-y-2">
                    <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors">
                      <Phone size={16} />
                      {COMPANY_INFO.phone}
                    </a>
                    <Link
                      to="/contact"
                      className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors [overflow-wrap:anywhere]"
                      data-testid="property-agent-email"
                    >
                      <Mail size={16} className="flex-shrink-0" />
                      {COMPANY_INFO.email}
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
